import { useContext } from 'react';
import { styled } from "@mui/material";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import * as Yup from 'yup';
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import { NotificationContext } from '../../../../providers/notification';
import apiCall from '../../../../utils/apiCall';

interface ApprovalType {
    ApprovalTypName: string;
};

interface ComponentProps {
    handleClose: () => void;
    approvalTypeName: string;
    approvalTypeId: number;
}

const UpdateApprovalType: React.FC<ComponentProps> = ({ handleClose, approvalTypeId, approvalTypeName }) => {
    const { showNotification } = useContext(NotificationContext);
    const queryClient = useQueryClient();

    const updateApprovalType = (data: ApprovalType) => {
        return apiCall().put(`/ApprovalType/updateApprovalType/${approvalTypeId}`, data);
    };

    const updateApprovalTypeMutation = useMutation(['update-approval-type'], updateApprovalType, {retry: 0});

    const updateApprovalTypeHandler = async (values: FormikValues) => {
        const payload: ApprovalType = {
            ApprovalTypName: values?.name,
        };

        try {
            await updateApprovalTypeMutation.mutateAsync(payload, {
                onSuccess: () => {
                    showNotification({
                        message: 'Approval type successfully updated',
                        type: 'success',
                    });

                    queryClient.refetchQueries(['get-approval-types']);
                    handleClose();
                }
            })
        } catch (error: any){
            showNotification({
                message: error?.response?.data?.title || error?.message,
                type: 'error',
            });
        }
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().matches(/^[A-Za-z\s!@#$%^&*()/]+$/, 'Only text is allowed').required('Approval type name is required'),
    });

    const formik = useFormik<FormikValues>({
        initialValues: {
            name: approvalTypeName ? approvalTypeName : ''
        },
        onSubmit: (values) => {
            updateApprovalTypeHandler(values);
        },
        validationSchema,
    });

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <FormWrapper>
                    <Field 
                        name='name'
                        type='text'
                        as={Input}
                        label='Name'
                        placeholder='Name'
                    />
                    {/* <Field 
                        name='status'
                        type='text'
                        as={Select}
                        label='Status'
                    /> */}
                </FormWrapper>
                <ButtonWrapper>
                    <ButtonContainer>
                        <StyledButton onClick={handleClose} text='Cancel'/>
                        <StyledButton 
                            type="submit" 
                            text={updateApprovalTypeMutation.isLoading ? 'Updating' : 'Update'} 
                            disabled={updateApprovalTypeMutation.isLoading}
                        />
                    </ButtonContainer>
                </ButtonWrapper>
            </form>
        </FormikProvider>
    )
};

export default UpdateApprovalType;

const FormWrapper = styled('div')`
    margin-inline: 3.5rem;
`

const ButtonWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-block: 2.5rem;
    padding-inline: 3.5rem;
`

const ButtonContainer = styled('div')`
    display: flex;
    gap: 2rem;
`

const StyledButton = styled(Button)`
    padding-block: 1rem;
    padding-inline: 2rem;
`

